/* eslint no-undef: 0 */
import {toQueryString} from '@s-ui/js/lib/string'

import FetcherFactory from '../../fetcher/Fetchers/factory.js'
import factoryLogger from '../../logger/index.js'
import AdsEntitiesFactory from '../Entities/factory.js'
import AdsErrorsFactory from '../Errors/factory.js'
import AdsMappersFactory from '../Mappers/factory.js'
import AdsValueObjectsFactory from '../ValueObjects/factory.js'
import EmptyAdsRepository from './EmptyAdsRepository.js'
import HTTPAdsRepository from './HTTPAdsRepository.js'
import LocalStorageAdsRepository from './LocalStorageAdsRepository.js'

export default class AdsRepositoriesFactory {
  /**
   * @method
   * @param {Object} deps
   * @param {Config} deps.config
   * @return {HTTPAdsRepository}
   * @property {Config} config
   * @property {Fetcher} fetcher
   * @property {Fetcher} fetchOrFail
   * @property {Function} adsValueObject
   * @property {Function} adEntityFactory
   * @property {Function} adsSearchParamsHttpToValueObjectMapperFactory
   * @property {Function} adsSearchParamsValueObjectToHttpMapperFactory
   * @property {Function} adsSearchParamsValueObjectFactory
   * @property {Function} getAdDetailsGenericErrorFactory
   * @property {Function} getAdDetailsNotFoundErrorFactory
   * @property {Function} log
   */
  static httpAdsRepository = ({config}) =>
    new HTTPAdsRepository({
      // Config
      config,
      // Utils
      toQueryString,
      // Logs
      log: factoryLogger({prefix: 'HTTPAdsRepository'}),
      // Fetchers
      fetcher: FetcherFactory.httpFetcher({config}),
      fetchOrFail: FetcherFactory.fetchOrFail({config}),
      // Value Objects
      adsListValueObject: AdsValueObjectsFactory.adsListValueObject,
      adsSearchParamsValueObjectFactory: AdsValueObjectsFactory.adsSearchParamsValueObject,
      subscriptionAdsListValueObjectFactory: AdsValueObjectsFactory.subscriptionAdsListValueObject,
      // Enitites
      adEntityFactory: AdsEntitiesFactory.adEntity,
      // Mappers
      adsSearchParamsHttpToValueObjectMapperFactory: AdsMappersFactory.adsSearchParamsHttpToValueObjectMapper,
      adsSearchParamsValueObjectToHttpMapperFactory: AdsMappersFactory.adsSearchParamsValueObjectToHttpMapper,
      financingConditionEntityFactory: AdsEntitiesFactory.financingConditionEntity,
      financingTermsRawDataToFinancingConditionEntityMapper:
        AdsMappersFactory.financingTermsRawDataToFinancingConditionEntityMapper({config}),
      fromAdRawDataToAdEntityMapper: AdsMappersFactory.fromAdRawDataToAdEntityMapper(),
      fromRecommendedAdsRawDataToRecommendedAdsListVOMapper:
        AdsMappersFactory.fromRecommendedAdsRawDataToRecommendedAdsListVOMapper({config}),
      fromStatsResponseToVOMapper: AdsMappersFactory.fromStatsResponseToVOMapper({config}),
      httpAdDetailsResponseToAdDetailsAggregateMapper:
        AdsMappersFactory.httpAdDetailsResponseToAdDetailsAggregateMapper({
          config
        }),
      httpMakeModelAdsListingToAdsListValueObjectMapper:
        AdsMappersFactory.httpMakeModelAdsListingToAdsListValueObjectMapper({
          config
        }),
      hTTPRelatedAdsResponseJSONtoAdEntityMapper: AdsMappersFactory.hTTPRelatedAdsResponseJSONtoAdEntityMapper({
        config
      }),
      httpRelatedSubscriptionAdsResponseToSubscriptionAdsListValueObjectMapper:
        AdsMappersFactory.httpRelatedSubscriptionAdsResponseToSubscriptionAdsListValueObjectMapper({config}),
      httpSearchResultsResponseToAdsListValueObjectMapper:
        AdsMappersFactory.httpSearchResultsResponseToAdsListValueObjectMapper({
          config,
          adsListValueObject: AdsValueObjectsFactory.adsListValueObject,
          adEntity: AdsEntitiesFactory.adEntity
        }),
      httpSearchUrlResponseToSearchDataAdsValueObjectMapper:
        AdsMappersFactory.httpSearchUrlResponseToSearchDataAdsValueObjectMapper({config}),
      httpSubscriptionCommitmentOptionsResponseToSubscriptionCommitmentOptionsValueObjectMapper:
        AdsMappersFactory.httpSubscriptionCommitmentOptionsResponseToSubscriptionCommitmentOptionsValueObjectMapper({
          config
        }),
      httpSubscriptionSearchResultsResponseToSubscriptionAdsListValueObjectMapper:
        AdsMappersFactory.httpSubscriptionSearchResultsResponseToSubscriptionAdsListValueObjectMapper({config}),
      searchRequestJSONToSearchParamsRequestJSONMapper:
        AdsMappersFactory.searchRequestJSONToSearchParamsRequestJSONMapper({
          config
        }),
      // Errors
      getAdDetailsGenericErrorFactory: AdsErrorsFactory.getAdDetailsGenericError,
      getAdDetailsNotFoundErrorFactory: AdsErrorsFactory.getAdDetailsNotFoundError,
      getAdDetailsPartialContentErrorFactory: AdsErrorsFactory.getAdDetailsPartialContentError,
      getFinancingDataAdsError: AdsErrorsFactory.getFinancingDataAdsError,
      getRecommendedAdsGenericErrorFactory: AdsErrorsFactory.getRecommendedAdsGenericError,
      getSearchDataFromUrlError: AdsErrorsFactory.getSearchDataFromUrlError,
      getSearchResultsErrorFactory: AdsErrorsFactory.getSearchResultsError,
      getSearchStatsGenericErrorFactory: AdsErrorsFactory.getSearchStatsGenericError,
      getSubscriptionCommitmentOptionsAdsErrorFactory: AdsErrorsFactory.getSubscriptionCommitmentOptionsAdsError,
      getSubscriptionSearchResultsAdsErrorFactory: AdsErrorsFactory.getSubscriptionSearchResultsAdsError,
      relatedAdsError: AdsErrorsFactory.relatedAdsError,
      reportAdError: AdsErrorsFactory.reportAdError
    })

  /**
   * @method
   * @return {LocalStorageAdsRepository}
   * @property {Storage} localStorage
   * @property {Function} adsSearchParamsValueObjectFactory
   * @property {Function} lastAdsSearchLegacyParamsMapperFactory
   * @property {Function} log
   */
  static localStorageAdsRepository = ({config}) =>
    new LocalStorageAdsRepository({
      localStorage,
      adsSearchParamsValueObjectFactory: AdsValueObjectsFactory.adsSearchParamsValueObject,
      lastAdsSearchLegacyParamsMapperFactory: AdsMappersFactory.lastAdsSearchLegacyParamsMapper,
      lastSearchesDataToSearchesListValueObjectMapper:
        AdsMappersFactory.lastSearchesDataToSearchesListValueObjectMapper({
          config
        }),
      lastDetailsViewedDataToLastDetailsViewedListValueObjectMapper:
        AdsMappersFactory.lastDetailsViewedDataToLastDetailsViewedListValueObjectMapper({config}),
      log: factoryLogger({prefix: 'LocalStorageAdsRepository'})
    })

  /**
   * @return {EmptyAdsRepository}
   */
  static emptyAdsRepository = () => new EmptyAdsRepository()
}
